import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useLogin } from './useAuth';
import { ThemeToggle } from '@/app/ThemeToggle';

interface LoginValues {
  email: string;
  password: string;
}

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useLogin();
  const loginSchema = z.object({
    email: z
      .string()
      .min(1, t('efs.auth.login.validation.email_required'))
      .email(t('efs.auth.login.validation.email_invalid')),
    password: z.string().min(1, t('efs.auth.login.validation.password_required')),
  });

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      navigate('/', { replace: true });
    } catch {
      // ApiClientError surfaces via login.error; render handles UI.
    }
  });

  const errorMsg =
    login.error?.message ??
    (login.isError ? t('efs.auth.login.error_generic', { defaultValue: 'Login failed' }) : null);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-efs-bg-app px-4">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-efs-text-primary">
            {t('efs.common.app.name')}
          </h1>
          <p className="text-sm text-efs-text-muted">{t('efs.common.app.tagline')}</p>
        </div>

        <div className="efs-panel space-y-4 rounded-lg border border-efs-border bg-efs-bg-panel p-6">
          <h2 className="text-base font-semibold text-efs-text-primary">
            {t('efs.auth.login.title', { defaultValue: 'ログイン' })}
          </h2>

          {errorMsg ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>
                {t('efs.auth.login.error_title', { defaultValue: 'サインインできません' })}
              </AlertTitle>
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          ) : null}

          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.auth.login.email', { defaultValue: 'メール' })}</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="email" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.auth.login.password', { defaultValue: 'パスワード' })}
                    </FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={login.isPending || form.formState.isSubmitting}
              >
                {login.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('efs.auth.login.submit', { defaultValue: 'サインイン' })}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
